// Operator-only runtime administration (/v1/admin). Every route requires a
// human session whose user carries the is_operator flag — the server answers
// 403 otherwise, and 503 substrate_unavailable for the container routes when
// no runtime answers them. All values are wire units (bytes, seconds); the
// admin pages convert to MiB/minutes for display.

import { getJSON, sendJSON, request, query } from "./http.js";

const BASE = "/v1/admin";

/** PullStatus is the image-pin pull lifecycle stored in the settings row. */
export type PullStatus = "idle" | "pulling" | "ready" | "error";

/** The pinned bino-cli image: the served tag/digest plus any pull in flight. */
export interface ImageSettings {
  repo: string;
  tag: string;
  digest: string;
  pullTag: string;
  pullStatus: PullStatus;
  pullError: string;
  /** The bino version of the served pin ("v0.98.0"); "" for a digest pin without a checked version. */
  version: string;
  /**
   * Why the served pin is refused, e.g. below minVersion or not checked yet.
   * Non-empty means renders, builds, lint, previews and live reports answer
   * 503 until an operator pins a supported release or a version check passes;
   * this page keeps working.
   */
  versionError: string;
  /** The oldest bino release this server accepts as a pin ("v0.98.0"). */
  minVersion: string;
}

/** Global per-container resource limits (wire units: bytes and seconds). */
export interface GlobalLimits {
  cpuCores: number;
  memoryBytes: number;
  pidsLimit: number;
  idleTimeoutSeconds: number;
  wallClockTimeoutSeconds: number;
  maxConcurrent: number;
  /**
   * The Live Artefact governors (migration 0041). Unlike every field above, 0
   * is a legal value here — "no idle floor" and "unlimited always-on" — so the
   * server accepts them at 0 rather than rejecting the body. That is exactly
   * why they must be part of this interface: the PUT replaces the whole set,
   * so a client that omits them clears an operator's rationing silently on the
   * next unrelated save.
   */
  liveIdleTimeoutFloorSeconds: number;
  liveMaxAlwaysOn: number;
  /**
   * The repository limits, also legal at 0 and sent on every save for the
   * same reason: 0 branch workspaces per repository is the built-in cap of
   * 10, and a storage quota of 0 bytes is no quota.
   */
  repositoryMaxBranches: number;
  storageQuotaBytes: number;
}

/**
 * BackupStatus is the database backup state, read-only. Times are RFC3339
 * UTC, or null when no such run is recorded. After a database restore it
 * shows the runs recorded in the restored database until the next run.
 */
export interface BackupStatus {
  /** The backup schedule (cron, UTC); "" when database backups are off. */
  schedule: string;
  /** When the last database backup file was written. */
  lastSuccessAt: string | null;
  /** Its file name in pb_data/db-backups. */
  lastSuccessName: string;
  /** Its size in bytes. */
  lastSuccessBytes: number;
  /**
   * When the last failed run started. A run can write its file and still
   * fail a later stage, so this can equal lastSuccessAt.
   */
  lastFailureAt: string | null;
  /** The first stage of that run that failed: busy, database, copy, prune or sweep. */
  lastFailureStep: string;
  /** The errors of that run, cut to 1000 characters. */
  lastFailureError: string;
}

export interface AdminSettings {
  image: ImageSettings;
  limits: GlobalLimits;
  backup: BackupStatus;
}

/** getSettings returns the image pin, global limits and backup status (also the pull-status poll target). */
export function getSettings(): Promise<AdminSettings> {
  return getJSON<AdminSettings>(`${BASE}/settings`);
}

/**
 * updateLimits replaces the global limits; every field must be > 0 except the
 * two Live Artefact governors and the two repository limits, which may be 0. The 200
 * body is not part of the contract, so it is deliberately not parsed — callers
 * re-fetch getSettings for the authoritative post-save state.
 */
export async function updateLimits(limits: GlobalLimits): Promise<void> {
  await request(`${BASE}/settings/limits`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(limits),
  });
}

/**
 * pinImage starts an async pull of the given tag (vX.Y.Z or sha256:digest).
 * Answers 202 immediately — poll getSettings until pullStatus leaves "pulling";
 * the pin flips only after the pull succeeds, so running spawns are unaffected.
 * A tag below minVersion answers 400 invalid_tag. A digest is pulled and then
 * checked with `bino version`; a failed check or an older version ends in
 * pullStatus "error" and keeps the old pin.
 */
export function pinImage(tag: string): Promise<{ tag: string; pullStatus: PullStatus }> {
  return sendJSON<{ tag: string; pullStatus: PullStatus }>(`${BASE}/settings/image`, "PUT", { tag });
}

/** AdminTeam is a team search hit (operators are not members of every team). */
export interface AdminTeam {
  id: string;
  name: string;
}

/** searchTeams finds teams by name for the override picker. */
export async function searchTeams(q: string): Promise<AdminTeam[]> {
  const { items } = await getJSON<{ items: AdminTeam[] }>(`${BASE}/teams${query({ q })}`);
  return items;
}

/**
 * TeamLimitOverrides are the ten per-team overridable dimensions. null means
 * "inherit the global value" — the wire never carries 0 (no dimension may
 * legally be 0, which is how the DB encodes inherit internally). The PUT
 * replaces the whole set, so every dimension is always sent: an omitted one
 * reverts to inherit.
 */
export interface TeamLimitOverrides {
  cpuCores: number | null;
  memoryBytes: number | null;
  pidsLimit: number | null;
  idleTimeoutSeconds: number | null;
  wallClockTimeoutSeconds: number | null;
  liveIdleTimeoutFloorSeconds: number | null;
  liveMaxAlwaysOn: number | null;
  repositoryMaxBranches: number | null;
  storageQuotaBytes: number | null;
  /**
   * The report mails a team's deployments may send per UTC day. There is no
   * global setting: null inherits the built-in 100.
   */
  mailDailyBudget: number | null;
}

/** One override row: the raw override limits plus the fully resolved effective values. */
export interface TeamLimitRow {
  teamId: string;
  teamName: string;
  limits: TeamLimitOverrides;
  effective: Omit<GlobalLimits, "maxConcurrent"> & { mailDailyBudget: number };
}

/** listTeamLimits returns every team override row with resolved effective values. */
export async function listTeamLimits(): Promise<TeamLimitRow[]> {
  const { items } = await getJSON<{ items: TeamLimitRow[] }>(`${BASE}/team-limits`);
  return items;
}

/**
 * upsertTeamLimits creates or replaces a team's overrides (null = inherit).
 * Like updateLimits, the response body is uncontracted and unparsed — callers
 * re-fetch listTeamLimits.
 */
export async function upsertTeamLimits(
  teamId: string,
  overrides: TeamLimitOverrides,
): Promise<void> {
  await request(`${BASE}/team-limits/${encodeURIComponent(teamId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(overrides),
  });
}

/** deleteTeamLimits removes a team's overrides (back to all-inherit). */
export async function deleteTeamLimits(teamId: string): Promise<void> {
  await request(`${BASE}/team-limits/${encodeURIComponent(teamId)}`, { method: "DELETE" });
}

/** OverQuotaRepository is a repository above its size limit. */
export interface OverQuotaRepository {
  id: string;
  teamId: string;
  name: string;
  origin: "hosted" | "mirror";
  sizeBytes: number;
  overQuota: boolean;
}

/**
 * listOverQuotaRepositories returns the repositories flagged over their size
 * limit, newest first. Reads, builds and previews of such a repository work;
 * until it is below the limits again, only changes to the default branch that
 * do not make it larger are accepted, plus deletes of other branches. Empty
 * when there are no repositories.
 */
export async function listOverQuotaRepositories(): Promise<OverQuotaRepository[]> {
  const { items } = await getJSON<{ items: OverQuotaRepository[] }>(`${BASE}/repositories`);
  return items;
}

/** ReservedSecret is a secret whose name the cluster reserves for itself. */
export interface ReservedSecret {
  teamId: string;
  scope: "workspace" | "repository" | "team";
  /** The workspace, repository or team that holds the secret. */
  ownerId: string;
  name: string;
}

/**
 * listReservedSecrets returns every secret, across all teams, whose name the
 * cluster reserves for itself (HOME, PATH, BINO_* and the like). They stay
 * until their team deletes them. Values are never returned.
 */
export async function listReservedSecrets(): Promise<ReservedSecret[]> {
  const { items } = await getJSON<{ items: ReservedSecret[] }>(`${BASE}/reserved-secrets`);
  return items;
}

/** ContainerInfo is one live managed container; cpuPercent/memoryBytes/pids are −1 when unknown. */
export interface ContainerInfo {
  id: string;
  kind: string;
  teamId: string;
  teamName: string;
  workspaceId: string;
  workspaceName: string;
  image: string;
  startedAt: string;
  lastActivity: string;
  cpuPercent: number;
  memoryBytes: number;
  pids: number;
}

/** SweepReport summarizes one stale-container sweep pass. */
export interface SweepReport {
  startedAt: string;
  finishedAt: string;
  examined: number;
  /** Orphans removed. */
  orphansKilled: number;
  /**
   * The orphan ladder of a split deployment, where an orphan is stopped
   * first and removed later: orphans seen too few times yet, stopped by this
   * pass, stopped and kept until their grace ends, and left for a later pass
   * by the cap on stops per 30 seconds. 0 in a single process.
   */
  watching?: number;
  stopped?: number;
  held?: number;
  capped?: number;
  errors: string[];
}

export interface ContainersReport {
  items: ContainerInfo[];
  running: number;
  maxConcurrent: number;
  /** Absent until the first sweep has run. */
  lastSweep?: SweepReport | null;
  bootRecovery: {
    /** null when no boot reconcile ran (e.g. the local dev runtime). */
    at: string | null;
    /**
     * Sessions the previous process left running and this one adopted: the
     * always-on live containers in one process; with split custody every
     * adopted preview and live session.
     */
    containersAdopted: number;
    containersRemoved: number;
    /** Builds the boot recovery marked stale; it does not grow after boot. */
    staleBuildsMarked: number;
  };
}

/** listContainers is the single polled admin endpoint: live containers + sweep/boot stats. */
export function listContainers(): Promise<ContainersReport> {
  return getJSON<ContainersReport>(`${BASE}/containers`);
}

/** killContainer force-kills one managed container by id. */
export async function killContainer(id: string): Promise<void> {
  await request(`${BASE}/containers/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** runSweep triggers an immediate sweep and returns its report. */
export function runSweep(): Promise<SweepReport> {
  return sendJSON<SweepReport>(`${BASE}/sweep`, "POST", {});
}
